import * as React from 'react';
import {
  dsiReportProvider,
  IDsiReportState,
} from '../../../data/providers/reports/dsiReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { DsiGraphCard } from './DsiGraphCard';
import { DsiResultList } from './DsiResultList';
import { DSISettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const DsiReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IDsiReportState>();
  /**
   * @description subscribe to report state
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = dsiReportProvider(
      props.entityType,
      props.entity.id
    ).subscribe((state: IDsiReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename('dsi', props.entity, reportState?.timeframe);

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Dsi Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <DsiGraphCard pdfMode={props.pdfMode} reportState={reportState} />
              <DsiResultList
                reportState={reportState}
                pdfMode={props.pdfMode}
                entity={props.entity}
              />
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  reportName='Dynamic Strength Index'
                  noComparison
                />
                <DsiGraphCard
                  pdfMode={props.pdfMode}
                  reportState={reportState}
                />
                <DsiResultList
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = () => setLoading(true);
  switch (errorCode) {
    case 'missing-date-range':
      return (
        <ErrorMessage
          text='Please select a valid timeframe for this report'
          iconName='date_range'
        >
          <DSISettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            This report requires 2 testing sessions for both CMJ and Isometric
            tests to have occured during the selected timeframe. Try choosing a
            wider timeframe.
          </div>
          <DSISettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
